
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useTheme } from "@/contexts/ThemeContext";
import { Eye, EyeOff } from "lucide-react";
import { Separator } from "@/components/ui/separator";

const Auth = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const isLightMode = theme === 'light';
  
  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate('/kol-tracker');
      }
    };
    
    checkAuth();
  }, [navigate]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      console.log("Attempting login for email:", email);
      
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });
      
      if (error) {
        console.error("Login error:", error);
        
        // Provide more specific error messages
        let errorMessage = error.message;
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = "Invalid email or password. Please check your credentials and try again.";
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = "Please check your email and confirm your account first.";
        } else if (error.message.includes('Too many requests')) {
          errorMessage = "Too many login attempts. Please wait a moment and try again.";
        }
        
        toast({
          title: "Login Failed",
          description: errorMessage,
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      if (!data.user) {
        console.error("No user data returned after successful auth");
        toast({
          title: "Login Failed",
          description: "No user data received. Please try again.",
          variant: "destructive",
        });
        setIsLoading(false);
        return;
      }

      console.log("Login successful for user:", data.user.id);

      // Get user profile data
      const { data: kolPicData, error: kolPicError } = await supabase
        .from('kol_pic')
        .select('access, name')
        .eq('id', data.user.id)
        .single();

      if (kolPicError) {
        console.error("Error fetching user profile:", kolPicError);
        // Don't fail login if profile fetch fails, just use defaults
      }

      // Store user data in localStorage
      localStorage.setItem('dashboard_user_access', kolPicData?.access || 'limited');
      localStorage.setItem('dashboard_user_name', kolPicData?.name || '');
      localStorage.setItem('dashboard_authorized', 'true');
      localStorage.setItem('dashboard_user_id', data.user.id);
      
      toast({
        title: "Login Successful",
        description: "Welcome back!",
      });
      
      navigate('/kol-tracker');
    } catch (err) {
      console.error("Unexpected login error:", err);
      toast({
        title: "Error",
        description: "An unexpected error occurred during login. Please check your internet connection and try again.",
        variant: "destructive",
      });
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setIsGoogleLoading(true);
    
    try {
      console.log("Attempting Google Sign-in");
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/kol-tracker`
        }
      });
      
      if (error) {
        console.error("Google Sign-in error:", error);
        
        let errorMessage = "Failed to sign in with Google. Please try again.";
        if (error.message.includes('Access denied')) {
          errorMessage = "Access denied. Your email is not authorized for this application. Please contact an administrator.";
        }
        
        toast({
          title: "Google Sign-in Failed",
          description: errorMessage,
          variant: "destructive",
        });
        setIsGoogleLoading(false);
        return;
      }

      // Note: For OAuth, the user will be redirected to Google, 
      // so we don't handle success here - it will be handled by the auth state change
      console.log("Google Sign-in initiated successfully");
      
    } catch (err) {
      console.error("Unexpected Google Sign-in error:", err);
      toast({
        title: "Error",
        description: "An unexpected error occurred during Google Sign-in. Please try again.",
        variant: "destructive",
      });
      setIsGoogleLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-dashboard-bg p-4">
      <div className="w-full max-w-md">
        <div className="dashboard-card shadow-md animate-fade-in">
          <div className="flex flex-col items-center justify-center space-y-6 p-8">
            <div className="h-28 w-32">
              {isLightMode ? (
                <img 
                  src="/lovable-uploads/ef8f3356-2693-4a98-8bc7-d4b32fbe8397.png" 
                  alt="Mantle Logo Light" 
                  className="h-full w-full object-contain"
                />
              ) : (
                <img 
                  src="/lovable-uploads/57a7aaa6-bf38-4409-a65a-b5c45e8026c6.png" 
                  alt="Mantle Logo Dark" 
                  className="h-full w-full object-contain"
                />
              )}
            </div>
            
            <h1 className="text-2xl font-bold text-center">KOL Dashboard</h1>

            {/* Google Sign-in Button */}
            <Button 
              type="button"
              variant="outline"
              onClick={handleGoogleSignIn}
              className="w-full bg-dashboard-bg border border-dashboard-border hover:bg-muted/50 text-foreground font-medium py-2 flex items-center gap-3"
              disabled={isGoogleLoading || isLoading}
            >
              <svg 
                width="18" 
                height="18" 
                viewBox="0 0 24 24" 
                fill="currentColor"
              >
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
              </svg>
              {isGoogleLoading ? "Signing in..." : "Continue with Google"}
            </Button>

            <div className="flex items-center w-full">
              <Separator className="flex-1" />
              <span className="px-3 text-sm text-muted-foreground">or</span>
              <Separator className="flex-1" />
            </div>

            <form onSubmit={handleLogin} className="space-y-5 w-full">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium">
                  Email Address
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-dashboard-bg border border-dashboard-border"
                  disabled={isLoading || isGoogleLoading}
                  required
                />
              </div>
              
              <div className="space-y-2">
                <label htmlFor="password" className="text-sm font-medium">
                  Password
                </label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="bg-dashboard-bg border border-dashboard-border pr-10"
                    disabled={isLoading || isGoogleLoading}
                    required
                  />
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    onClick={togglePasswordVisibility}
                  >
                    {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                  </button>
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-medium py-2"
                disabled={isLoading || isGoogleLoading}
              >
                {isLoading ? "Logging in..." : "Login"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Auth;
